// src/layouts/openPages/live/Live_Google.js
import React, { useState, useEffect, useCallback } from 'react';
import { getThing, startThingUpdates, stopUpdates } from './LiveApis';
import GoogleMap from './google_map'; // ✅ Using Google Map
import {
  Modal,
  Button,
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  useMediaQuery,
  Divider,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import mainLogo from "../../../assets/images/dhundhoo_logo.png";

const Puller = () => (
  <Box sx={{ width: 40, height: 6, backgroundColor: 'grey.300', borderRadius: 3, margin: '12px auto' }} />
);

function Live_Google() {
  const [thingModel, setThingModel] = useState(null);
  const [error, setError] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [fromMeLocation, setFromMeLocation] = useState({ lat: 0, lon: 0, flag: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [browsmapLink, setBrowsmapLink] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [updateLocation, setUpdateLocation] = useState(null);
  const [selectedStopId, setSelectedStopId] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    const authParams = new URLSearchParams(window.location.search);
    const weblink = `https://web.dhundhoo.com/live_ol_google?apiKey=${authParams.get('apiKey')}&thingId=${authParams.get('thingId')}`;
    setBrowsmapLink(weblink);

    getThing(
      (data) => {
        if (data.currentJourney?.sequence) {
          const keys = Object.keys(data.currentJourney.sequence);
          if (keys.length > 0) {
            const ordered = {};
            keys.sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
              .forEach((key, index) => {
                ordered[key] = data.currentJourney.sequence[key];
                if (data.stops?.[ordered[key]]) data.stops[ordered[key]].index = index;
              });
            data.currentJourney.sequence = ordered;
          }
        }
        setThingModel(data);
        setError(null);
        if (data.latest?.GPS?.location) setCurrentLocation(data.latest.GPS.location);
        startThingUpdates((loc) => setUpdateLocation(loc));
      },
      (err) => setError(err)
    );

    const timer = setTimeout(() => setIsLoading(true), 5000);
    return () => { clearTimeout(timer); stopUpdates(); };
  }, []);

  useEffect(() => { if (updateLocation) setCurrentLocation(updateLocation); }, [updateLocation]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setFromMeLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude, flag: 1 }),
        () => setFromMeLocation({ lat: 0, lon: 0, flag: 1 }),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    }
  }, []);

  const getData = useCallback(() => {
    if (fromMeLocation.flag !== 1 || !currentLocation) return;
    const v = currentLocation;
    const glink = `https://www.google.com/maps/embed?pb=!1m26!1m12!1m3!1d952990.5372896822!2d77.48398416359312!3d21.08786269702407!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!4m11!3e0!4m4!2s${v.latitude}%2C${v.longitude}!3m2!1d${v.latitude}!2d${v.longitude}!4m4!2s${fromMeLocation.lat}%2C${fromMeLocation.lon}!3m2!1d${fromMeLocation.lat}!2d${fromMeLocation.lon}!5e0!3m2!1sen!2sin!4v1668452361266!5m2!1sen!2sin`;
    setModalVisible(true);
    setTimeout(() => {
      const iframe = document.getElementById('googleiframe');
      if (iframe) iframe.src = glink;
    }, 100);
  }, [currentLocation, fromMeLocation]);

  const handleStopClick = (stopId) => {
    setSelectedStopId(stopId);
  };

  const deviceStatus = thingModel?.updateMessage || 'Device connected';
  const isConnected = deviceStatus === 'Device connected';
  const sequenceKeys = thingModel?.currentJourney?.sequence ? Object.keys(thingModel.currentJourney.sequence) : [];

  return (
    <Box sx={{ width: '100%', height: '100vh', position: 'relative', overflow: 'hidden' }}>

      {/* MAP SECTION */}
      {!error && currentLocation && thingModel ? (
        <>
          <Box sx={{ width: '100%', height: '100%' }}>
            {/* ✅ Google Map exactly replacing Openlayer */}
            <GoogleMap
              position={currentLocation}
              thingModel={thingModel}
              selectedStopId={selectedStopId}
              onStopSelect={setSelectedStopId}
              userLocation={fromMeLocation.flag === 1 ? { latitude: fromMeLocation.lat, longitude: fromMeLocation.lon } : null}
            />
          </Box>

          {/* FLOATING TOP LOGO & TRACKING BUTTON */}
          <Box sx={{ position: 'absolute', top: 16, left: 16, right: 16, zIndex: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Paper elevation={3} sx={{ p: 0.5, borderRadius: 2, display: 'flex', alignItems: 'center', bgcolor: 'white' }}>
              <img src={mainLogo} alt="Logo" style={{ height: 35 }} />
            </Paper>
          </Box>

          {/* FLOATING DEVICE STATUS (Bottom Center) */}
          <Box sx={{ position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', width: '90%', maxWidth: 450, zIndex: 10 }}>
            <Paper
              elevation={6}
              sx={{
                p: 2, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                bgcolor: isConnected ? 'rgba(255,255,255,0.95)' : '#ffebee', backdropFilter: 'blur(8px)',
                border: '1px solid', borderColor: isConnected ? 'success.light' : 'error.light'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DirectionsBusIcon color={isConnected ? "success" : "error"} fontSize="large" />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color={isConnected ? "#aacea5" : "error.main"}>{deviceStatus}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <b>
                      {thingModel.thing?.name} • {thingModel.currentJourney?.journeyName}
                    </b>
                  </Typography>
                </Box>
              </Box>
              <IconButton color="primary" onClick={() => setDrawerOpen(true)} sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}>
                <MenuIcon />
              </IconButton>
            </Paper>
          </Box>

          {/* STOPS DRAWER WITH SCROLL FIX */}
          {drawerOpen && (
            <Box
              onClick={() => setDrawerOpen(false)}
              sx={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.2)',
                backdropFilter: 'blur(4px)',
                zIndex: 1200,
              }}
            />
          )}

          <Box
            sx={{
              position: 'fixed',
              bottom: 30,
              left: '50%',
              transform: drawerOpen ? 'translate(-50%, 0%)' : 'translate(-50%, 100%)',
              width: '90%',
              maxWidth: 450,
              height: '70vh',
              bgcolor: '#fff',
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              boxShadow: '0 -12px 40px rgba(0,0,0,0.25)',
              transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 1300,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Drawer Header (Fixed Height, Won't Shrink) */}
            <Box sx={{ flexShrink: 0 }}>
              <Puller />
              <Box sx={{ px: 3, pb: 1, pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">Route Stops</Typography>
                <IconButton onClick={() => setDrawerOpen(false)} sx={{ bgcolor: 'grey.100', width: 32, height: 32 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Divider />
            </Box>

            {/* Scrollable List Area */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: 2,
                pt: 2,
                pb: 3,
                '&::-webkit-scrollbar': { width: '5px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: '#bdbdbd', borderRadius: '10px' },
              }}
            >
              <List disablePadding>
                {sequenceKeys.map((seqIndex) => {
                  const stopId = thingModel.currentJourney.sequence[seqIndex];
                  const stop = thingModel.stops[stopId];
                  const index = parseInt(seqIndex, 10);
                  const isSelected = selectedStopId === stopId;

                  return (
                    <ListItem
                      key={stopId}
                      button
                      onClick={() => handleStopClick(stopId)}
                      sx={{
                        mb: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'grey.200',
                        backgroundColor: isSelected ? 'primary.50' : '#fff',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: isSelected ? 'primary.50' : 'grey.50',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      {/* Number Badge */}
                      <Box
                        sx={{
                          minWidth: 28, height: 28, borderRadius: '50%',
                          bgcolor: isSelected ? 'primary.main' : 'grey.200',
                          color: isSelected ? '#fff' : 'text.secondary',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 'bold', fontSize: '0.85rem', mr: 2
                        }}
                      >
                        {index + 1}
                      </Box>

                      {/* Text & Icon */}
                      <ListItemText
                        primary={<Typography variant="subtitle2" fontWeight="600">{stop?.locationName || "Unknown Stop"}</Typography>}
                      // secondary={
                      //   <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      //     <LocationOnIcon sx={{ fontSize: 14, mr: 0.5, color: isSelected ? 'primary.main' : 'text.disabled' }} />
                      //     {stop?.address || "No address available"}
                      //   </Typography>
                      // } 
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Box>
        </>
      ) : (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%">
          {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
        </Box>
      )}

      {/* Distance Modal */}
      <Modal open={modalVisible} onClose={() => setModalVisible(false)}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '95%', maxWidth: 800, bgcolor: 'background.paper', borderRadius: 4, p: 2 }}>
          <iframe id="googleiframe" title="Distance" width="100%" height="500" style={{ border: 0, borderRadius: 12 }} />
          <Button fullWidth onClick={() => setModalVisible(false)} sx={{ mt: 2 }}>Close</Button>
        </Box>
      </Modal>
    </Box>
  );
}

export default Live_Google;